import { makeStyles, tokens } from '@fluentui/react-components';

export const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },

  editorRoot: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    minWidth: 0,
    gap: tokens.spacingVerticalS,
  },

  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },

  tableContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },

  statusBadge: {
    textTransform: 'capitalize' as const,
  },

  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXL,
  },

  emptyStateIcon: {
    fontSize: tokens.fontSizeHero800,
    color: tokens.colorNeutralForeground3,
  },

  emptyStateTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold as unknown as number,
    color: tokens.colorNeutralForeground1,
  },

  emptyStateDescription: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center' as const,
    maxWidth: '480px',
  },

  loadingState: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalL,
  },

  errorState: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },

  dialogForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },

  filePickerRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'flex-end',
  },

  filePickerInput: {
    flex: 1,
  },

  progressSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },

  progressText: {
    color: tokens.colorNeutralForeground3,
  },

  // ── Table ───────────────────────────────────────────────────────────────

  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },

  tableHeader: {
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },

  tableHeaderCell: {
    textAlign: 'left',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },

  tableRow: {
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },

  tableRowExpanded: {
    borderBottomWidth: '0',
  },

  tableCell: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    verticalAlign: 'middle',
    fontSize: tokens.fontSizeBase300,
  },

  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },

  chevronButton: {
    padding: 0,
    minWidth: 'unset',
  },

  // ── Expansion panel ─────────────────────────────────────────────────────

  expansionRow: {
    backgroundColor: tokens.colorNeutralBackground2,
  },

  expansionCell: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    maxWidth: '1px',
    width: '100%',
  },

  expansionPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minWidth: 0,
    overflow: 'hidden',
  },

  expansionLoading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    padding: tokens.spacingVerticalM,
  },

  // ── Metadata grid ───────────────────────────────────────────────────────

  metadataGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
  },

  metadataLabel: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },

  metadataValue: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },

  monoValue: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
  },

  // ── Date row (timestamps + Edit button) ─────────────────────────────────

  dateRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  dateGroup: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
  },

  // ── Inline edit form ─────────────────────────────────────────────────────

  editForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },

  editFormActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },

  editError: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },

  // ── Actions row ─────────────────────────────────────────────────────────

  actionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },

  // ── Sub-tab data table ──────────────────────────────────────────────────

  dataTabToolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
  },

  dataIdCell: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },

  // ── Files section (inside expanded data row) ────────────────────────────

  filesSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },

  filesHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  filesTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },

  fileList: {
    display: 'flex',
    flexDirection: 'column',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
  },

  fileItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },

  fileInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flexGrow: 1,
    minWidth: 0,
  },

  fileName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },

  fileId: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },

  fileActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },

  // ── Column width helpers ────────────────────────────────────────────────

  chevronColumnCell: {
    width: '32px',
  },

  actionsColumnCell: {
    width: '48px',
  },

  // ── Raw data picker (AddAnnotationDataDialog) ──────────────────────────

  pickerContainer: {
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    borderRadius: tokens.borderRadiusMedium,
    maxHeight: '220px',
    overflowY: 'auto',
  },

  pickerLoadingCenter: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center' as const,
  },

  pickerEmptyText: {
    padding: tokens.spacingVerticalM,
    display: 'block',
    color: tokens.colorNeutralForeground3,
  },

  pickerRow: {
    cursor: 'pointer',
  },

  pickerRowSelected: {
    cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },

  dialogSurfaceWide: {
    maxWidth: '640px',
  },

  monoTextarea: {
    fontFamily: tokens.fontFamilyMonospace,
  },

  // ── AnnotationSetExpandedRow helpers ────────────────────────────────────

  destructiveButton: {
    backgroundColor: tokens.colorStatusDangerBackground3,
  },

  subtleLabel: {
    color: tokens.colorNeutralForeground3,
  },

  versionDropdown: {
    minWidth: '240px',
  },

  dangerText: {
    color: tokens.colorStatusDangerForeground1,
  },

  // ── UploadAnnotationDialog helpers ─────────────────────────────────────

  hiddenInput: {
    display: 'none',
  },

  preWrapText: {
    whiteSpace: 'pre-wrap',
  },
});
